import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { glossary } from '../data/glossary';
import { getProduct } from '../data/products';
import { Sparkles, ArrowRight, Leaf, Calendar } from 'lucide-react';

// Same eligibility & ordering rules as the FeaturedHerb component
function getWeekNumber() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = (now - start) + ((start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000);
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  return Math.floor(diff / oneWeek);
}

function dateOfWeek(weekIndex) {
  const year = new Date().getFullYear();
  const jan1 = new Date(year, 0, 1);
  const d = new Date(jan1.getTime() + weekIndex * 7 * 24 * 60 * 60 * 1000);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const HerbArchive = () => {
  const [filter, setFilter] = useState('all'); // all | past | upcoming | current

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const eligible = useMemo(
    () => glossary.filter(g => g.latin && g.foundIn && g.foundIn.length > 0),
    []
  );

  const currentWeek = getWeekNumber();

  const timeline = useMemo(() => {
    // Show a year's worth of upcoming featured herbs (52 weeks starting from some early week)
    // plus a few past weeks so it feels like an archive.
    const startWeek = Math.max(0, currentWeek - 12);
    const items = [];
    for (let w = startWeek; w < startWeek + 52; w++) {
      const herb = eligible[w % eligible.length];
      items.push({ week: w, herb, isCurrent: w === currentWeek, isPast: w < currentWeek });
    }
    return items;
  }, [eligible, currentWeek]);

  const filtered = timeline.filter(t => {
    if (filter === 'past') return t.isPast;
    if (filter === 'current') return t.isCurrent;
    if (filter === 'upcoming') return t.week > currentWeek;
    return true;
  });

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#efe9db] to-[#faf7f1]">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">
            <Calendar className="w-4 h-4" /> The Herb Archive
          </div>
          <h1 className="font-serif-elegant text-5xl md:text-7xl mb-4">Herb of the Week</h1>
          <p className="text-[#4a4640] max-w-2xl mx-auto leading-relaxed">
            A rotating tribute to the plants we love. Every week a new botanical steps into the light &mdash;
            past and future featured herbs live here.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10 justify-center">
          {[
            { key: 'all', label: 'All' },
            { key: 'past', label: 'Past' },
            { key: 'current', label: 'This Week' },
            { key: 'upcoming', label: 'Upcoming' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-5 py-1.5 text-xs uppercase tracking-widest border transition-colors ${filter === t.key ? 'bg-[#3a4a3b] border-[#3a4a3b] text-[#faf7f1]' : 'border-[#c9c1ad] text-[#4a4640] hover:border-[#3a4a3b]'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Gallery */}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map(({ week, herb, isCurrent, isPast }) => {
            const product = herb.foundIn.map(getProduct).find(Boolean);
            return (
              <li
                key={week}
                className={`bg-[#faf7f1] border ${isCurrent ? 'border-[#3a4a3b] ring-2 ring-[#3a4a3b]/20' : 'border-[#e5decf]'} p-6 hover:shadow-lg transition-shadow flex flex-col`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#7a8b6b]">
                    <Sparkles className="w-3 h-3" /> Week of {dateOfWeek(week)}
                  </div>
                  {isCurrent && <span className="badge-sale">This week</span>}
                  {isPast && <span className="text-[10px] uppercase tracking-widest text-[#6f6a5f]">Past</span>}
                </div>
                {product && (
                  <div className="aspect-[4/3] bg-[#efe9db] mb-5 flex items-center justify-center p-4 overflow-hidden">
                    <img src={product.images[0]} alt={product.title} className="w-full h-full object-contain" />
                  </div>
                )}
                <h3 className="font-serif-elegant text-2xl mb-1">{herb.name}</h3>
                {herb.latin && <div className="font-script text-lg text-[#7a8b6b] mb-3">{herb.latin}</div>}
                <p className="text-sm text-[#4a4640] leading-relaxed mb-4 line-clamp-4">{herb.benefits}</p>
                <div className="mt-auto flex flex-wrap items-center gap-2">
                  {product && (
                    <Link to={`/products/${product.id}`} className="text-xs uppercase tracking-widest text-[#3a4a3b] link-underline inline-flex items-center gap-1">
                      {product.title} <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {/* Bottom CTA */}
        <section className="mt-20 text-center bg-[#efe9db] py-14 px-6">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">
            <Leaf className="w-4 h-4" /> Never miss a herb
          </div>
          <h2 className="font-serif-elegant text-3xl md:text-4xl mb-3">Join the herbal circle</h2>
          <p className="text-[#4a4640] max-w-lg mx-auto mb-6">Explore the full glossary of every botanical we use, or take our ritual quiz to find the herb calling to you.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/glossary" className="btn-outline">Explore Glossary</Link>
            <Link to="/quiz" className="btn-primary">Take the Quiz</Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HerbArchive;
